<script>
import Layout from "../../layouts/main.vue";
import PageHeader from "@/components/page-header";
import appConfig from "../../../app.config";
import Swal from "sweetalert2";
import axios from "axios";
import { formatDateToWeb } from "@/helpers/date-functions.js";

import { usePermissions } from "@/composables/usePermissions";

export default {
    page: {
        title: "Alternative Device Request Details",
        meta: [{ name: "description", content: appConfig.description }],
    },

    components: {
        Layout,
        PageHeader,
    },

    setup() {
        const permissions = usePermissions();
        return {
            permissions,
        };
    },

    data() {
        return {
            title: "Request Details",
            items: [
                {
                    text: "Device Management",
                    href: "/",
                },
                {
                    text: "Alternative Devices",
                    href: "/device/alternative-devices",
                },
                {
                    text: "Request Details",
                    active: true,
                },
            ],
            loading: false,
            submitting: false,
            requestData: null,
            buActionRemark: "",
        };
    },

    mounted() {
        this.fetchRequestDetails();
    },

    computed: {
        isPendingBuApproval() {
            if (!this.requestData || !this.requestData.status) return false;
            return this.requestData.status.slug === "awaiting_bu_approval";
        },
        hasBuReviewed() {
            if (!this.requestData || !this.requestData.status) return false;
            const slug = this.requestData.status.slug;
            return slug === "bu_approved" || slug === "bu_rejected" || slug === "awaiting_customer_approval" || slug === "customer_approved" || slug === "customer_rejected" || !!this.requestData.approved_by;
        },
        isBuApproved() {
            if (!this.requestData || !this.requestData.status) return false;
            const slug = this.requestData.status.slug;
            return slug === "bu_approved" || slug === "awaiting_customer_approval" || slug === "customer_approved";
        },
        isAwaitingCustomerApproval() {
            if (!this.requestData || !this.requestData.status) return false;
            return this.requestData.status.slug === "awaiting_customer_approval";
        },
        customerConfirmationLink() {
            return this.requestData?.customer_confirmation_link || "";
        },
        shouldShowCopyLink() {
            if (!this.requestData || !this.requestData.status) return false;
            const slug = this.requestData.status.slug;
            return ["awaiting_customer_approval", "customer_approved", "customer_rejected"].includes(slug);
        },
        isCustomerDecisionFinal() {
            if (!this.requestData || !this.requestData.status) return false;
            const slug = this.requestData.status.slug;
            return slug === "customer_approved" || slug === "customer_rejected";
        },
    },

    methods: {
        setAuthHeader() {
            const token = localStorage.getItem("jwt");
            if (token) {
                axios.defaults.headers.common["authorization"] = "Bearer " + token;
            }
        },

        async fetchRequestDetails() {
            const requestId = this.$route.params.id;
            if (!requestId) return;

            this.loading = true;
            try {
                this.setAuthHeader();
                const res = await axios.get(process.env.VUE_APP_API_BASEURL + `request-forms/${requestId}`);
                if (res.data && res.data.success) {
                    this.requestData = res.data.data;
                } else {
                    this.requestData = null;
                }
            } catch (error) {
                console.error("Failed to fetch request details:", error);
                this.requestData = null;
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: error.response?.data?.message || "Failed to load request details.",
                    customClass: { confirmButton: "btn btn-primary w-xs" },
                    buttonsStyling: false,
                });
            } finally {
                this.loading = false;
            }
        },

        formatDate(dateStr) {
            if (!dateStr) return "N/A";
            return formatDateToWeb(dateStr);
        },

        formatCurrency(amount) {
            if (!amount && amount !== 0) return "RM 0.00";
            const num = parseFloat(amount);
            return isNaN(num) ? "RM 0.00" : `RM ${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        },

        getPriceDiff() {
            if (!this.requestData?.current_device?.price || !this.requestData?.new_device?.price) return null;
            const originalPrice = parseFloat(this.requestData.current_device.price) || 0;
            const newPrice = parseFloat(this.requestData.new_device.price) || 0;
            const diff = newPrice - originalPrice;
            if (Math.abs(diff) < 0.01) {
                return { type: 'same', icon: 'ri-equal-line', textClass: 'text-muted' };
            } else if (diff > 0) {
                return { type: 'higher', icon: 'ri-arrow-up-line', textClass: 'text-danger' };
            } else {
                return { type: 'lower', icon: 'ri-arrow-down-line', textClass: 'text-success' };
            }
        },

        getCostDiff() {
            if (!this.requestData?.current_device?.device_cost || !this.requestData?.new_device?.device_cost) return null;
            const originalCost = parseFloat(this.requestData.current_device.device_cost) || 0;
            const newCost = parseFloat(this.requestData.new_device.device_cost) || 0;
            const diff = newCost - originalCost;
            if (Math.abs(diff) < 0.01) {
                return { type: 'same', icon: 'ri-equal-line', textClass: 'text-muted' };
            } else if (diff > 0) {
                return { type: 'higher', icon: 'ri-arrow-up-line', textClass: 'text-danger' };
            } else {
                return { type: 'lower', icon: 'ri-arrow-down-line', textClass: 'text-success' };
            }
        },

        getCreditBadgeClass(status) {
            switch (status) {
                case "N1":
                    return "bg-success-subtle text-success border border-success-subtle";
                case "N2":
                    return "bg-warning-subtle text-warning border border-warning-subtle";
                case "N3":
                    return "bg-danger-subtle text-danger border border-danger-subtle";
                default:
                    return "bg-secondary-subtle text-secondary border border-secondary-subtle";
            }
        },

        getStatusBadgeClass(statusObj) {
            const slug = typeof statusObj === 'object' && statusObj !== null ? statusObj.slug : String(statusObj || '').toLowerCase();
            switch (slug) {
                case "awaiting_bu_approval":
                    return "bg-warning-subtle text-warning border border-warning-subtle";
                case "bu_approved":
                    return "bg-success-subtle text-success border border-success-subtle";
                case "bu_rejected":
                    return "bg-danger-subtle text-danger border border-danger-subtle";
                case "awaiting_customer_approval":
                    return "bg-info-subtle text-info border border-info-subtle";
                case "customer_approved":
                    return "bg-primary-subtle text-primary border border-primary-subtle";
                case "customer_rejected":
                    return "bg-danger-subtle text-danger border border-danger-subtle";
                default:
                    return "bg-secondary-subtle text-secondary border border-secondary-subtle";
            }
        },

        getStatusLabel(statusObj) {
            if (!statusObj) return "N/A";
            if (typeof statusObj === 'object') {
                return statusObj.name || statusObj.status_name || statusObj.slug || "N/A";
            }
            return String(statusObj);
        },

        handleApprove() {
            if (!this.permissions.canApproveAlternativeDevices?.value) {
                Swal.fire({
                    icon: "error",
                    title: "Access Denied",
                    text: "You do not have permission to approve alternative device requests.",
                    customClass: { confirmButton: "btn btn-primary w-xs" },
                    buttonsStyling: false,
                });
                return;
            }

            Swal.fire({
                title: "Approve Request?",
                text: `Are you sure you want to APPROVE alternative device request #${this.requestData.request_number}?`,
                icon: "question",
                showCancelButton: true,
                confirmButtonText: "Yes, Approve Request",
                cancelButtonText: "Cancel",
                customClass: {
                    confirmButton: "btn btn-success w-xs me-2",
                    cancelButton: "btn btn-soft-secondary w-xs",
                },
                buttonsStyling: false,
            }).then((result) => {
                if (result.isConfirmed) {
                    this.submitBUDecision("approve");
                }
            });
        },

        handleReject() {
            if (!this.permissions.canApproveAlternativeDevices?.value) {
                Swal.fire({
                    icon: "error",
                    title: "Access Denied",
                    text: "You do not have permission to reject alternative device requests.",
                    customClass: { confirmButton: "btn btn-primary w-xs" },
                    buttonsStyling: false,
                });
                return;
            }

            if (!this.buActionRemark.trim()) {
                Swal.fire({
                    icon: "warning",
                    title: "Remark Required",
                    text: "Please provide a reason / remark before rejecting this request.",
                    customClass: {
                        confirmButton: "btn btn-primary w-xs",
                    },
                    buttonsStyling: false,
                });
                return;
            }

            Swal.fire({
                title: "Reject Request?",
                text: `Are you sure you want to REJECT alternative device request #${this.requestData.request_number}?`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, Reject Request",
                cancelButtonText: "Cancel",
                customClass: {
                    confirmButton: "btn btn-danger w-xs me-2",
                    cancelButton: "btn btn-soft-secondary w-xs",
                },
                buttonsStyling: false,
            }).then((result) => {
                if (result.isConfirmed) {
                    this.submitBUDecision("reject");
                }
            });
        },

        async submitBUDecision(decision) {
            this.submitting = true;
            try {
                this.setAuthHeader();
                const res = await axios.post(
                    process.env.VUE_APP_API_BASEURL + `request-forms/${this.requestData.id}/bu-decision`,
                    {
                        decision: decision,
                        remarks: this.buActionRemark,
                    }
                );

                if (res.data && res.data.success) {
                    this.requestData = res.data.data;
                    this.buActionRemark = "";
                    const isApproved = decision === "approve" || decision === "bu_approved";
                    Swal.fire({
                        title: isApproved ? "Approved!" : "Rejected!",
                        text: res.data.message || "Decision submitted successfully.",
                        icon: isApproved ? "success" : "error",
                        customClass: {
                            confirmButton: isApproved ? "btn btn-success w-xs" : "btn btn-danger w-xs",
                        },
                        buttonsStyling: false,
                    });
                }
            } catch (error) {
                console.error("Failed to submit BU decision:", error);
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: error.response?.data?.message || "Failed to submit decision.",
                    customClass: { confirmButton: "btn btn-primary w-xs" },
                    buttonsStyling: false,
                });
            } finally {
                this.submitting = false;
            }
        },

        copyCustomerLink() {
            if (!this.customerConfirmationLink) return;
            navigator.clipboard.writeText(this.customerConfirmationLink).then(() => {
                Swal.fire({
                    icon: "success",
                    title: "Link Copied!",
                    text: "Customer confirmation link has been copied to clipboard.",
                    customClass: {
                        confirmButton: "btn btn-primary w-xs",
                    },
                    buttonsStyling: false,
                });
            }).catch((err) => {
                console.error("Failed to copy link:", err);
            });
        },

        handleNotifyCustomer() {
            Swal.fire({
                title: "Notify Customer?",
                text: "Are you sure you want to send confirmation notification to customer via email?",
                icon: "info",
                showCancelButton: true,
                confirmButtonText: "Yes, Send Notification",
                cancelButtonText: "Cancel",
                customClass: {
                    confirmButton: "btn btn-primary w-xs me-2",
                    cancelButton: "btn btn-soft-secondary w-xs",
                },
                buttonsStyling: false,
            }).then((result) => {
                if (result.isConfirmed) {
                    this.submitRequesterDecision("notify");
                }
            });
        },

        async submitRequesterDecision(decision) {
            this.submitting = true;
            try {
                this.setAuthHeader();
                
                if(this.isAwaitingCustomerApproval ) {
                    const res = await axios.post(
                        process.env.VUE_APP_API_BASEURL + `request-forms/${this.requestData.id}/resend-notification`,                        
                    );
                    if (res.data && res.data.success) {
                        this.requestData = res.data.data;
                        Swal.fire({
                            title: "Notification Sent!",
                            text: res.data.message || "Customer has been notified successfully.",
                            icon: "success",
                            customClass: {
                                confirmButton: "btn btn-success w-xs",
                            },
                            buttonsStyling: false,
                        });
                    }

                }else{
                    const res = await axios.post(
                        process.env.VUE_APP_API_BASEURL + `request-forms/${this.requestData.id}/requester-decision`,
                        {
                            decision: decision,
                            remarks: "Customer notified for alternative device confirmation",
                        }
                    );

                    if (res.data && res.data.success) {
                        this.requestData = res.data.data;
                        Swal.fire({
                            title: "Notification Sent!",
                            text: res.data.message || "Customer has been notified successfully.",
                            icon: "success",
                            customClass: {
                                confirmButton: "btn btn-success w-xs",
                            },
                            buttonsStyling: false,
                        });
                    }
                }
            } catch (error) {
                console.error("Failed to notify customer:", error);
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: error.response?.data?.message || "Failed to send notification to customer.",
                    customClass: { confirmButton: "btn btn-primary w-xs" },
                    buttonsStyling: false,
                });
            } finally {
                this.submitting = false;
            }
        },
    },
};
</script>

<template>
    <Layout>
        <PageHeader :title="title" :items="items" />

        <!-- Loading Spinner -->
        <div class="card card-body text-center py-5 d-flex align-items-center justify-content-center flex-column" v-if="loading">
            <div class="spinner-border text-primary mb-2" role="status"></div>
            <p class="text-muted mb-0">Loading request details...</p>
        </div>

        <!-- Error / Empty State -->
        <div class="card card-body text-center py-5" v-else-if="!requestData">
            <i class="ri-error-warning-line fs-48 text-muted mb-2"></i>
            <h5 class="text-dark fw-bold mb-1">Request Not Found</h5>
            <p class="text-muted mb-3">The requested alternative device form could not be found or loaded.</p>
            <div>
                <router-link to="/device/alternative-devices" class="btn btn-primary btn-sm">
                    <i class="ri-arrow-left-line me-1"></i> Back to Requests List
                </router-link>
            </div>
        </div>

        <div class="row" v-else>
            <div class="col-lg-12">
                <!-- Header Banner Card: Request Info & Navigation -->
                <div class="card card-body">
                    <div class="d-flex flex-wrap align-items-center justify-content-between gap-3">
                        <div class="d-flex align-items-center gap-3">
                            <router-link to="/device/alternative-devices" class="btn btn-soft-secondary btn-icon waves-effect">
                                <i class="ri-arrow-left-line fs-18"></i>
                            </router-link>
                            <div>
                                <h4 class="mb-1 text-dark fw-bold">
                                    Request #{{ requestData.request_number }}
                                </h4>
                                <div class="d-flex flex-wrap align-items-center gap-2 text-muted fs-13">
                                    <span><i class="ri-calendar-event-line me-1"></i>Requested: {{ formatDate(requestData.created_at) }}</span>
                                    <span>•</span>
                                    <span><i class="ri-user-3-line me-1"></i>Requested By: <strong class="text-dark">{{ typeof requestData.created_by === 'object' ? requestData.created_by?.name : (requestData.created_by || requestData.creator?.name || 'CS Agent') }}</strong></span>
                                </div>
                            </div>
                        </div>

                        <div class="d-flex align-items-center gap-2">
                            <span class="fs-12 text-muted me-1">Current Status:</span>
                            <span class="badge fs-13 px-3 py-2" :class="getStatusBadgeClass(requestData.status)">
                                {{ getStatusLabel(requestData.status) }}
                            </span>
                        </div>
                    </div>
                </div>

                <!-- SECTION 1: All Request & Device Comparison Details -->
                <div class="card">
                    <div class="card-header align-items-center d-flex bg-light-subtle">
                        <h5 class="card-title mb-0 flex-grow-1">
                            <i class="ri-information-line align-middle me-2 text-primary"></i> Request &amp; Device Comparison Details
                        </h5>
                        <div class="d-flex align-items-center gap-2" v-if="requestData.order">
                            <span class="badge bg-primary fs-12 px-3 py-1">Order # {{ requestData.order.orderId || ('#' + requestData.order.id) }}</span>
                            <router-link :to="'/orders/customer-order-details/' + (requestData.order.customer?.id || requestData.order.customer_id)" target="_blank" class="btn btn-soft-primary btn-sm">
                                <i class="ri-external-link-line align-middle me-1"></i> View Order
                            </router-link>
                        </div>
                    </div>

                    <div class="card-body">
                        <!-- Order & Request General Overview Grid -->
                        <div class="row g-3 mb-4 p-3 bg-light-subtle rounded border">
                            <div class="col-md-3 col-6">
                                <span class="text-muted fs-12 d-block">Linked Order ID</span>
                                <span class="fw-bold text-primary fs-14">#{{ requestData.order ? (requestData.order.orderId || requestData.order.id) : 'N/A' }}</span>
                            </div>
                            <div class="col-md-3 col-6">
                                <span class="text-muted fs-12 d-block">Request Type</span>
                                <span class="fw-semibold text-dark fs-14">{{ requestData.request_type }}</span>
                            </div>
                            <div class="col-md-3 col-6">
                                <span class="text-muted fs-12 d-block">Customer Name</span>
                                <span class="fw-semibold text-dark fs-14">{{ requestData.order && requestData.order.customer ? requestData.order.customer.name : 'N/A' }}</span>
                            </div>
                            <div class="col-md-3 col-6">
                                <span class="text-muted fs-12 d-block">Customer NRIC</span>
                                <span class="fw-medium text-dark fs-13">{{ requestData.order && requestData.order.customer ? (requestData.order.customer.nric || 'N/A') : 'N/A' }}</span>
                            </div>
                            <div class="col-md-3 col-6">
                                <span class="text-muted fs-12 d-block">Customer Contact</span>
                                <span class="fw-medium text-dark fs-13">{{ requestData.order && requestData.order.customer ? (requestData.order.customer.phone_number || requestData.order.customer.phone || 'N/A') : 'N/A' }}</span>
                            </div>
                            <div class="col-md-3 col-6">
                                <span class="text-muted fs-12 d-block">Customer Email</span>
                                <span class="fw-medium text-dark fs-13">{{ requestData.order && requestData.order.customer ? (requestData.order.customer.email || 'N/A') : 'N/A' }}</span>
                            </div>
                            <div class="col-md-3 col-6">
                                <span class="text-muted fs-12 d-block">Tenure</span>
                                <span class="fw-semibold text-dark fs-14">{{ requestData.order && requestData.order.tenure ? (requestData.order.tenure + ' months') : 'N/A' }}</span>
                            </div>

                            <div class="col-md-3 col-6">
                                <span class="text-muted fs-12 d-block">Order Status</span>
                                <span class="badge bg-primary-subtle text-primary border border-primary-subtle fs-12 px-2 py-1">
                                    {{ requestData.order && requestData.order.status_name ? requestData.order.status_name : 'N/A' }}
                                </span>
                            </div>
                            <div class="col-md-3 col-6">
                                <span class="text-muted fs-12 d-block">Credit Check Tier &amp; Result</span>
                                <div class="d-flex align-items-center gap-1 mt-1 flex-wrap">
                                    <span class="badge fs-12 px-2 py-1" :class="getCreditBadgeClass(requestData.order?.risk_tier)" v-if="requestData.order?.risk_tier">
                                        {{ requestData.order.risk_tier }}
                                    </span>
                                    <span class="badge bg-info-subtle text-info border border-info-subtle fs-12 px-2 py-1" v-if="requestData.order?.credit_check_result">
                                        {{ requestData.order.credit_check_result }}
                                    </span>
                                    <span class="text-muted fs-13" v-if="!requestData.order?.risk_tier && !requestData.order?.credit_check_result">N/A</span>
                                </div>
                            </div>
                            <div class="col-md-3 col-6">
                                <span class="text-muted fs-12 d-block">Max Monthly Affordable</span>
                                <span class="fw-bold text-success fs-14">
                                    {{ requestData.order && (requestData.order.max_monthly_affordable !== null && requestData.order.max_monthly_affordable !== undefined) ? `${formatCurrency(requestData.order.max_monthly_affordable)} / month` : 'N/A' }}
                                </span>
                            </div>
                            <div class="col-md-3 col-6">
                                <span class="text-muted fs-12 d-block">Order Created Date</span>
                                <span class="fw-medium text-dark fs-13">
                                    {{ requestData.order && requestData.order.created_at ? formatDate(requestData.order.created_at) : 'N/A' }}
                                </span>
                            </div>

                        </div>

                        <!-- Devices Side-by-Side Comparison -->
                        <h6 class="text-muted text-uppercase fw-semibold mb-3 fs-12">
                            <i class="ri-device-line me-1"></i> Device Comparison (Current vs Requested)
                        </h6>
                        <div class="row g-4 mb-4">
                            <!-- Current Device Card -->
                            <div class="col-md-6">
                                <div class="p-3 border rounded bg-white h-100 shadow-sm" v-if="requestData.current_device || requestData.currentDevice">
                                    <div class="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
                                        <span class="fs-12 fw-semibold text-muted text-uppercase">Original Device</span>
                                        <span class="badge bg-danger-subtle text-danger border border-danger-subtle px-2 py-1">
                                            Current Device
                                        </span>
                                    </div>
                                    <div class="d-flex align-items-start mb-3">
                                        <div class="avatar-md me-3 flex-shrink-0">
                                            <div class="avatar-title bg-danger-subtle text-danger rounded fs-24">
                                                <i class="ri-cellphone-line"></i>
                                            </div>
                                        </div>
                                        <div class="flex-grow-1">
                                            <h5 class="fs-15 mb-1 text-dark fw-bold">
                                                {{ (requestData.current_device?.product_name || requestData.current_device?.product?.product_name || requestData.currentDevice?.product?.product_name) || 'Current Device' }}
                                            </h5>
                                            <span class="badge bg-light text-muted border">
                                                SKU: {{ requestData.current_device?.sku || requestData.currentDevice?.sku }}
                                            </span>
                                        </div>
                                    </div>
                                    <div class="row g-2 pt-2 border-top">
                                        <div class="col-6">
                                            <span class="text-muted fs-12 d-block">Device Cost</span>
                                            <span class="fw-semibold text-dark fs-13">
                                                {{ formatCurrency(requestData.current_device?.device_cost) }}
                                            </span>
                                        </div>
                                        <div class="col-6">
                                            <span class="text-muted fs-12 d-block">Monthly Charges</span>
                                            <span class="fw-bold text-danger fs-14">
                                                {{ formatCurrency(requestData.current_device?.price || requestData.currentDevice?.price) }} / mo
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div class="alert alert-warning mb-0" v-else>No Current Device Data</div>
                            </div>

                            <!-- Requested Alternative Device Card -->
                            <div class="col-md-6">
                                <div class="p-3 border rounded h-100 shadow-sm border-success" style="background-color: rgba(10, 207, 151, 0.08);" v-if="requestData.new_device || requestData.newDevice">
                                    <div class="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
                                        <span class="fs-12 fw-semibold text-success text-uppercase">New Selected Device</span>
                                        <span class="badge bg-success-subtle text-success border border-success-subtle px-2 py-1">
                                            Requested Alternative Device
                                        </span>
                                    </div>
                                    <div class="d-flex align-items-start mb-3">
                                        <div class="avatar-md me-3 flex-shrink-0">
                                            <div class="avatar-title bg-success-subtle text-success rounded fs-24">
                                                <i class="ri-checkbox-circle-fill"></i>
                                            </div>
                                        </div>
                                        <div class="flex-grow-1">
                                            <h5 class="fs-15 mb-1 text-dark fw-bold">
                                                {{ (requestData.new_device?.product_name || requestData.new_device?.product?.product_name || requestData.newDevice?.product?.product_name) || 'Requested Device' }}
                                            </h5>
                                            <span class="badge bg-success-subtle text-success border border-success-subtle">
                                                SKU: {{ requestData.new_device?.sku || requestData.newDevice?.sku }}
                                            </span>
                                        </div>
                                    </div>
                                    <div class="row g-2 pt-2 border-top">
                                        <div class="col-6">
                                            <span class="text-muted fs-12 d-block">Device Cost</span>
                                            <span class="fw-semibold fs-13" :class="getCostDiff() ? getCostDiff().textClass : 'text-dark'">
                                                <i v-if="getCostDiff()" :class="[getCostDiff().icon, 'me-1']"></i>{{ formatCurrency(requestData.new_device?.device_cost) }}
                                            </span>
                                            <div class="text-muted fs-11" v-if="requestData.current_device?.device_cost">
                                                (Current: {{ formatCurrency(requestData.current_device?.device_cost) }})
                                            </div>
                                        </div>
                                        <div class="col-6">
                                            <span class="text-muted fs-12 d-block">Monthly Charges</span>
                                            <span class="fw-bold fs-14" :class="getPriceDiff() ? getPriceDiff().textClass : 'text-success'">
                                                <i v-if="getPriceDiff()" :class="[getPriceDiff().icon, 'me-1']"></i>{{ formatCurrency(requestData.new_device?.price || requestData.newDevice?.price) }} / mo
                                            </span>
                                            <div class="text-muted fs-11" v-if="requestData.current_device?.price || requestData.currentDevice?.price">
                                                (Current: {{ formatCurrency(requestData.current_device?.price || requestData.currentDevice?.price) }}/mo)
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="alert alert-warning mb-0" v-else>No New Device Data</div>
                            </div>
                        </div>

                        <!-- CS Remark Box -->
                        <div class="mb-2">
                            <h6 class="text-muted text-uppercase fw-semibold mb-2 fs-12">
                                <i class="ri-chat-3-line me-1"></i> CS Requester Remark
                            </h6>
                            <div class="p-3 bg-light rounded border text-dark fs-14">
                                {{ requestData.remarks || 'No additional remarks provided by requester.' }}
                            </div>
                        </div>
                    </div>
                </div>

                <!-- SECTION 2: BU Review & Approval Panel -->

                <!-- Case A: Pending Approval (Show Action Panel) -->
                <div class="card border border-warning" v-if="isPendingBuApproval">
                    <div class="card-header align-items-center d-flex bg-warning-subtle border-warning-subtle">
                        <h5 class="card-title mb-0 flex-grow-1 text-warning">
                            <i class="ri-shield-user-line align-middle me-2"></i> Business Unit (BU) Review &amp; Approval Action
                        </h5>
                        <span class="badge bg-warning text-dark fs-12">Action Required</span>
                    </div>

                    <div class="card-body">
                        <p class="text-muted mb-3 fs-13">
                            Please review the alternative device request above. Choose whether to <strong>Approve</strong> or <strong>Reject</strong> this request. Providing a remark is required when rejecting.
                        </p>

                        <div class="row g-3">
                            <div class="col-12">
                                <label class="form-label fw-semibold">BU Review Remark / Reason</label>
                                <textarea
                                    class="form-control"
                                    rows="3"
                                    v-model="buActionRemark"
                                    placeholder="Enter review remarks or reason for approval / rejection..."
                                    :disabled="submitting || !permissions.canApproveAlternativeDevices?.value"
                                ></textarea>
                            </div>

                            <div class="col-12 text-end pt-2">
                                <button type="button" class="btn btn-danger px-4 me-2" @click="handleReject" :disabled="submitting || !permissions.canApproveAlternativeDevices?.value">
                                    <span v-if="submitting" class="spinner-border spinner-border-sm me-1" role="status"></span>
                                    <i v-else class="ri-close-circle-fill align-middle me-1"></i> Reject Request
                                </button>
                                <button type="button" class="btn btn-success px-4" @click="handleApprove" :disabled="submitting || !permissions.canApproveAlternativeDevices?.value">
                                    <span v-if="submitting" class="spinner-border spinner-border-sm me-1" role="status"></span>
                                    <i v-else class="ri-checkbox-circle-fill align-middle me-1"></i> Approve Request
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Case B: Already Reviewed by BU (Show Historic Record) -->
                <div
                    class="card"
                    :class="requestData.status && (requestData.status.slug === 'bu_approved' || requestData.status.slug === 'customer_approved' || requestData.status.slug === 'awaiting_customer_approval') ? 'border border-success' : 'border border-danger'"
                    v-else-if="hasBuReviewed"
                >
                    <div
                        class="card-header align-items-center d-flex"
                        :class="requestData.status && (requestData.status.slug === 'bu_approved' || requestData.status.slug === 'customer_approved' || requestData.status.slug === 'awaiting_customer_approval') ? 'bg-success-subtle' : 'bg-danger-subtle'"
                    >
                        <h5
                            class="card-title mb-0 flex-grow-1"
                            :class="requestData.status && (requestData.status.slug === 'bu_approved' || requestData.status.slug === 'customer_approved' || requestData.status.slug === 'awaiting_customer_approval') ? 'text-success' : 'text-danger'"
                        >
                            <i
                                :class="requestData.status && (requestData.status.slug === 'bu_approved' || requestData.status.slug === 'customer_approved' || requestData.status.slug === 'awaiting_customer_approval') ? 'ri-checkbox-circle-fill me-2' : 'ri-close-circle-fill me-2'"
                            ></i>
                            Business Unit (BU) Decision Details
                        </h5>
                        <span
                            class="badge fs-12 px-3 py-1"
                            :class="requestData.status && (requestData.status.slug === 'bu_approved' || requestData.status.slug === 'customer_approved') ? 'bg-success text-white' : 'bg-danger text-white'"
                        >
                            {{ getStatusLabel(requestData.status) }}
                        </span>
                    </div>

                    <div class="card-body">
                        <div class="row g-3">
                            <div class="col-md-4 col-6">
                                <span class="text-muted fs-12 d-block">Reviewed / Approved By</span>
                                <span class="fw-bold text-dark fs-14">
                                    {{ (typeof requestData.approved_by === 'object' ? requestData.approved_by?.name : requestData.approved_by) || 'BU Approver' }}
                                </span>
                            </div>

                            <div class="col-md-4 col-6">
                                <span class="text-muted fs-12 d-block">Decision Status</span>
                                <span
                                    class="badge fs-12 px-2.5 py-1"
                                    :class="getStatusBadgeClass(requestData.status)"
                                >
                                    {{ getStatusLabel(requestData.status) }}
                                </span>
                            </div>

                            <div class="col-md-4 col-12">
                                <span class="text-muted fs-12 d-block">
                                    {{ requestData.status && requestData.status.slug === 'bu_approved' ? 'Approved At' : (requestData.status && requestData.status.slug === 'bu_rejected' ? 'Rejected At' : 'Decision Date') }}
                                </span>
                                <span class="fw-semibold text-dark fs-14">
                                    <i class="ri-time-line me-1 text-muted"></i>{{ formatDate(requestData.updated_at || requestData.created_at) }}
                                </span>
                            </div>

                            <div class="col-12 mt-3 pt-3 border-top">
                                <span class="text-muted fs-12 d-block mb-1">BU Official Remark</span>
                                <div class="p-3 bg-light rounded text-dark fs-14">
                                    {{ requestData.remarks_bu || 'No remark was attached.' }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- SECTION 3: Requester Action / Customer Confirmation Panel (Shown after BU Approved) -->
                <div class="card border border-info mt-3" v-if="isBuApproved">
                    <div class="card-header align-items-center d-flex bg-info-subtle border-info-subtle">
                        <h5 class="card-title mb-0 flex-grow-1 text-info">
                            <i class="ri-mail-send-line align-middle me-2"></i> Requester Action — Customer Confirmation
                        </h5>
                        <span class="badge bg-info text-white fs-12" v-if="isAwaitingCustomerApproval">
                            Customer Notified
                        </span>
                        <span class="badge bg-primary text-white fs-12" v-else>
                            Ready to Notify
                        </span>
                    </div>

                    <div class="card-body">
                        <p class="text-muted mb-3 fs-13">
                            BU approval has been granted. As the requester/CS agent, {{ shouldShowCopyLink ? 'you can copy the confirmation link or notify the customer via email to confirm the alternative device offer.' : 'you can notify the customer via email to confirm the alternative device offer.' }}
                        </p>

                        <div class="row g-3 align-items-center">
                            <div class="col-md-8 col-12" v-if="shouldShowCopyLink">
                                <label class="form-label fs-12 fw-semibold text-muted mb-1">Customer Confirmation Link</label>
                                <div class="input-group">
                                    <input
                                        type="text"
                                        class="form-control bg-light"
                                        :value="customerConfirmationLink"
                                        readonly
                                    />
                                    <button class="btn btn-outline-secondary" type="button" @click="copyCustomerLink">
                                        <i class="ri-file-copy-line me-1"></i> Copy Link
                                    </button>
                                </div>
                            </div>

                            <div :class="shouldShowCopyLink ? 'col-md-4 col-12 text-md-end text-start pt-md-4 pt-0' : 'col-12 d-flex justify-content-md-end justify-content-start'">
                                <button
                                    type="button"
                                    :class="shouldShowCopyLink ? 'btn btn-primary px-4 w-100 w-md-auto text-nowrap' : 'btn btn-primary px-4 w-100 notify-btn text-nowrap'"
                                    @click="handleNotifyCustomer"
                                    :disabled="submitting || isCustomerDecisionFinal"
                                >
                                    <span v-if="submitting" class="spinner-border spinner-border-sm me-1" role="status"></span>
                                    <i v-else-if="isAwaitingCustomerApproval" class="ri-refresh-line me-1"></i>
                                    <i v-else class="ri-notification-3-line me-1"></i>
                                    {{ isAwaitingCustomerApproval ? 'Resend Notification' : 'Notify Customer' }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Layout>
</template>

<style scoped>
.avatar-title {
    display: flex;
    align-items: center;
    justify-content: center;
}

.notify-btn {
    white-space: nowrap;
}

@media (min-width: 768px) {
    .notify-btn {
        max-width: 30%;
    }
}
</style>
